import { Component, ElementRef, Injector, Input, OnDestroy, OnInit, inject } from '@angular/core';

import { BetSlipType, TaxationModel } from '@bpos';
import { BrazilRegulatoryData, SlipState } from '@bpos/v1/my-bets';
import { EpcotConfig, GeolocationConfig, MyBetsConfig, Sitecore, SitecoreMyBetsConfig } from '@frontend/sports/common/client-config-data-access';
import { MoneyService } from '@frontend/sports/common/core/feature/money';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { ModalDialogService } from '@frontend/sports/modal/feature';
import { OptionQuery } from '@frontend/sports/navigation/core/feature/pick-uri';
import { CustomerOfferService } from '@frontend/sports/new-customer-offer-feature';
import { ToasterService } from '@frontend/sports/toaster/feature';
import { NOT_APPLICABLE, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { EarlyPayout } from '@frontend/sports/types/components/sitecore';
import { BetslipListType } from '@frontend/sports/types/models/my-bets';
import { SyncUserSettingsService } from '@frontend/sports/user/feature';
import { WINDOW } from '@frontend/vanilla/core';
import { ClipboardService } from '@frontend/vanilla/shared/clipboard';
import { Decimal } from 'decimal.js';
import { PromoTokenPlaceBetSignPostingInfo } from 'packages/sports/common/betslip/modules/reward-tokens/reward-tokens.model';
import { Observable, map } from 'rxjs';

import { EarlyPayoutUtils } from '../../../my-bets-base/early-payout/early-payout-utils';
import { EarlyPayoutHelpers } from '../../../my-bets-base/early-payout/my-bets-earlypayout-helpers';
import { EarlyPayoutErrorTypes, EarlyPayoutOptionErrors } from '../../../my-bets-base/models/early-payout-types';
import { MyBetsBet, MyBetsBetSlip, MyBetsListContext, PayoutType, TokenType } from '../../../my-bets-base/models/my-bets-viewmodels';
import { AlertConfigService } from '../../../native-alerts/alert-config.service';
import { IPlacedBetInfo } from '../../../share-my-bet/share-my-bet.state';
import { TokensSummaryService } from '../../../tokens-base/tokens-summary.service';
import { MyBetsTrackingLocation } from '../../models/my-bets-tracking.model';
import { BetslipUtilService } from '../../services/betslip-util.service';
import { MyBetsApiService } from '../../services/my-bets-api.service';
import { MyBetsStrategy } from '../../services/my-bets-display.strategy';
import { MyBetsEditBetHistoryService } from '../../services/my-bets-edit-bet-history.service';
import { MyBetsPrintService } from '../../services/my-bets-print.service';
import { MyBetsUtilService } from '../../services/my-bets-util.service';
import { MyBetsEditBetHistoryListComponent } from '../edit-bet/my-bets-edit-bet-history-list.component';
import { AlertData } from './my-bets-toolbar.models';

@Component({
    selector: 'ms-my-bets-toolbar',
    templateUrl: 'my-bets-toolbar.html',
    styleUrl: './my-bets-toolbar.component.scss',
    standalone: false,
})
export class MyBetsToolbarComponent implements OnInit, OnDestroy {
    @Input() betslip: MyBetsBetSlip;
    @Input() myBetsListContext: MyBetsListContext;
    isExpanded: boolean;
    taxationInfo: string;
    alertData: { bets: AlertData[] } | null;
    cashoutInfoVisible = false;
    enableRedesignedBetCard: boolean;

    private editBetHistoryTooltipTimeout = 6000;
    private timeout: any;
    deviceOSVersion$: Observable<string>;

    private injector = inject(Injector);

    constructor(
        public sitecore: Sitecore,
        public sitecoreMyBets: SitecoreMyBetsConfig,
        private toaster: ToasterService,
        private clipboardService: ClipboardService,
        private tracker: TrackingService,
        private nativeAlertService: AlertConfigService,
        private myBetsConfig: MyBetsConfig,
        private editBetHistoryService: MyBetsEditBetHistoryService,
        private modalDialogService: ModalDialogService,
        private myBetsStrategy: MyBetsStrategy,
        private printService: MyBetsPrintService,
        public myBetsUtilService: MyBetsUtilService,
        private tokensSummaryService: TokensSummaryService,
        private customerOfferService: CustomerOfferService,
        private earlyPayoutUtils: EarlyPayoutUtils,
        private betslipUtilService: BetslipUtilService,
        private elementRef: ElementRef,
        private moneyService: MoneyService,
        private earlyPayoutHelpers: EarlyPayoutHelpers,
        private timerService: TimerService,
        private userSettings: SyncUserSettingsService,
        private myBetsApiService: MyBetsApiService,
        private geolocationConfig: GeolocationConfig,
        private epcotConfig: EpcotConfig,
    ) {}

    ngOnInit(): void {
        if (this.hasTaxationInfo) {
            this.taxationInfo = this.getTaxationInfo();
        }

        this.alertData = this.getAlertData();
        this.isExpanded = this.myBetsListContext.isPrintView;

        this.timeout = this.timerService.setTimeoutOutsideAngular(
            () => this.editBetHistoryService.saveTooltipRead(),
            this.editBetHistoryTooltipTimeout,
        );
        this.enableRedesignedBetCard = this.myBetsUtilService.isBetCardRedesigned();
        this.deviceOSVersion$ = this.myBetsApiService.fetchDeviceDetailsByBetSlipId(this.betslipId).pipe(
            map((deviceDetails: BrazilRegulatoryData | undefined) => {
                return deviceDetails?.operatingSystem || '';
            }),
        );
    }

    ngOnDestroy(): void {
        clearTimeout(this.timeout);
        this._window.removeEventListener('click', this.closeCashoutTooltip, true);
    }

    clickPrintButton(event: Event): void {
        event.stopPropagation();
        event.preventDefault();
        this.printService.printMyBetsSingleCard(this.betslip.betslipRealId, this.myBetsListContext.associatedAccount);
    }

    clickCashOutInfo(event: Event): void {
        this.updateCashoutInfoVisibility(!this.cashoutInfoVisible);
    }

    get hasValidExportIconType(): boolean {
        return !!(
            this.myBetsListContext.listType === BetslipListType.Settled &&
            (this.myBetsListContext.showDownloadButton || this.myBetsListContext.showPrintButton)
        );
    }

    toggleToolbar(event?: Event): void {
        event?.stopPropagation();
        event?.preventDefault();

        this.isExpanded = !this.isExpanded;
        const context = this.myBetsListContext.isEventView ? 'EventView' : 'myBets';
        const pageName = `${context}_OptionsDropdown_${this.isExpanded ? 'expanded' : 'Collapsed'}`;
        this.tracker.update({ [trackingConstants.PAGE_REFERRING_ACTION]: pageName });
    }

    get shouldShowDeviceDetails(): boolean {
        return !!this.betslipId && this.isExpanded && !!this.geolocationConfig.saveDeviceDetails;
    }

    copyToClipboard(event: Event): void {
        event.stopPropagation();
        event.preventDefault();

        if (this.clipboardService.copy(this.betslipId)) {
            this.toaster.open(this.sitecoreMyBets.myBets.BetSlipIdCopied, { duration: 3000 });
        } else {
            this.toaster.open('copy NOT supported!', { duration: 3000 });
        }

        const context = this.myBetsListContext.isEventView ? 'EventView' : 'myBets';
        this.tracker.update({ [trackingConstants.PAGE_REFERRING_ACTION]: context + '_BetIDCopy' });

        this.betslipIdCopyTracking();
    }

    private betslipIdCopyTracking(): void {
        const trackingData = {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: trackingConstants.BETTING,
            [trackingConstants.COMPONENT_LABEL_EVENT]: trackingConstants.MYBETS,
            [trackingConstants.COMPONENT_ACTION_EVENT]: trackingConstants.EVENT_CLICK,
            [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getBetslipListType(),
            [trackingConstants.COMPONENT_EVENT_DETAILS]: trackingConstants.COPY_BETSLIP_ID,
            [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
        };
        this.tracker.track(trackingConstants.EVENT_TRACKING, trackingData);
    }

    private getBetslipListType(): string {
        if (this.myBetsListContext.isEventView) return 'edp';
        return this.myBetsListContext.listType ? `${this.myBetsListContext.listType.toLowerCase()} tab` : NOT_APPLICABLE;
    }

    async editBetHistory(event: Event): Promise<void> {
        event.stopPropagation();
        event.preventDefault();

        await this.editBetHistoryService.getEditBetBetslips(this.betslipId, this.myBetsListContext.associatedAccount);

        if (!this.myBetsStrategy.inColumn) {
            const epcotClass = this.epcotConfig.isEnabled ? 'epcot-enabled' : '';
            this.modalDialogService.openDialog(MyBetsEditBetHistoryListComponent, {
                data: { myBetsListContext: this.myBetsListContext },
                settings: {
                    isModalDialog: false,
                    cssClass: `edit-bet-history-modal ${epcotClass}`,
                    fit: false,
                    showDialogHeader: true,
                    title: this.sitecoreMyBets.myBets.EditBetHistoryTitle,
                    injector: this.injector,
                },
            });
        }
    }

    editBetHistoryTooltipClick(event: Event): void {
        event.stopPropagation();
        event.preventDefault();
        this.editBetHistoryService.saveTooltipRead();
    }

    get showEditBetHistoryButton(): boolean {
        return this.betslip.isEditBet;
    }

    get showCashoutUnavailableInfo(): boolean {
        if (!this.earlyPayoutUtils.isEarlyPayoutAllowed()) {
            return false;
        }

        if (
            this.betslip.betType === BetSlipType.System ||
            (this.myBetsListContext.listType !== BetslipListType.Settled && !this.betslip.isPotentialEP && !this.betslip.earlyPayout)
        ) {
            return true;
        }

        const earlyPayoutError = this.betslip.earlyPayout?.error;

        return (
            !!earlyPayoutError &&
            !this.earlyPayoutHelpers.isLiveSgpError(earlyPayoutError.type, earlyPayoutError.reason) &&
            (earlyPayoutError.type === EarlyPayoutErrorTypes.BetNotAllowedForCashout ||
                earlyPayoutError.type === EarlyPayoutOptionErrors.OptionMarketTemplateNotAllowed)
        );
    }

    get cashoutUnavailableInfoTooltipText(): string {
        let tooltipText = '';
        if (this.showCashoutUnavailableInfo) {
            const error = this.betslip.earlyPayout?.error;

            if (error?.type === EarlyPayoutOptionErrors.OptionMarketTemplateNotAllowed) {
                tooltipText = this.sitecore.earlyPayout.MarketTemplateNotAllowed;
            } else if (this.betslip.betType === BetSlipType.System) {
                tooltipText = this.sitecore.earlyPayout.System;
            } else if (!this.betslip.isPotentialEP && !this.betslip.earlyPayout) {
                tooltipText = this.sitecore.earlyPayout.NotBasicModel;
            } else {
                tooltipText = this.sitecore.earlyPayout[error?.reason! as keyof EarlyPayout];
            }
        }

        return tooltipText;
    }

    get oddsBoostInfoContent(): string {
        const rewardTokenInfo = this.betslip.usedRewardToken?.additionalInformation;
        const currency = rewardTokenInfo?.boostedWinningsCurrencyCode;

        if (!rewardTokenInfo?.winningsBoost || !rewardTokenInfo?.boostedWinnings || !currency) {
            return '';
        }

        return interpolateDoubleBracesString(this.sitecore.crmTokens.myBets.OddsBoostInfoTemplate, {
            oldWinnings: this.moneyService.transform(rewardTokenInfo.boostedWinnings - rewardTokenInfo.winningsBoost, true, 2, false, '', currency),
            boostedWinnings: this.moneyService.transform(rewardTokenInfo.winningsBoost, true, 2, false, '', currency),
            payoutType: this.payoutType,
        });
    }

    get accaBoostInfoContent(): string {
        const rewardTokenInfo = this.betslip.usedRewardToken?.additionalInformation;
        const currency = rewardTokenInfo?.boostedWinningsCurrencyCode;

        if (!rewardTokenInfo?.winningsBoost || !rewardTokenInfo?.boostedWinnings || !currency) {
            return '';
        }

        const oldWinningsValue = new Decimal(rewardTokenInfo.boostedWinnings).minus(rewardTokenInfo.winningsBoost).toNumber();
        const oldWinnings = this.moneyService.transform(oldWinningsValue, true, 2, false, '', currency);
        const boostedWinnings = this.moneyService.transform(rewardTokenInfo.winningsBoost, true, 2, false, '', currency);

        return interpolateDoubleBracesString(this.sitecore.crmTokens.myBets.AccaBoostInfoTemplate, { oldWinnings, boostedWinnings });
    }

    get riskFreeInfoContent(): string {
        const rewardTokenInfo = this.betslip.usedRewardToken!.additionalInformation!;
        const placeBetSignPostingData = (this.betslip.placeBetSignPosting ?? {}) as PromoTokenPlaceBetSignPostingInfo;

        return this.tokensSummaryService.getRiskFreeSummary(
            this.betslip.usedRewardToken!.payoutType!,
            rewardTokenInfo.riskFreeMaxCompensation!,
            rewardTokenInfo.riskFreePercentage,
            this.betslip.stake.value,
            placeBetSignPostingData.noOfSlabs,
            placeBetSignPostingData.slabAmount,
        );
    }

    get payoutType(): string {
        const payoutType = this.betslip.usedRewardToken!.payoutType;
        const myBetsTranslation = this.sitecore.crmTokens.myBets;

        if (payoutType === PayoutType.Bonus) {
            return myBetsTranslation.PayoutTypeBonus;
        }

        if (payoutType === PayoutType.Cash) {
            return myBetsTranslation.PayoutTypeCash;
        }

        if (payoutType === PayoutType.FreeBet) {
            return myBetsTranslation.PayoutTypeFreeBet;
        }

        return '';
    }

    get oddsBoostInfoIncluded(): boolean {
        return !!(
            this.betslip.usedRewardToken?.tokenType === TokenType.OddsBoostToken &&
            !this.betslip.isCachedOut &&
            this.oddsBoostInfoContent &&
            (this.myBetsListContext.listType === BetslipListType.Open ||
                this.myBetsListContext.listType === BetslipListType.Live ||
                (this.myBetsListContext.listType === BetslipListType.Settled && this.betslip.state === SlipState.Won))
        );
    }

    get accaBoostInfoIncluded(): boolean {
        return this.betslipUtilService.isAccaBoostInfoIncluded(this.betslip, this.myBetsListContext);
    }

    get riskFreeInfoIncluded(): boolean {
        return !!(this.betslip.usedRewardToken && this.betslip.usedRewardToken.tokenType === TokenType.RiskFreeToken);
    }

    get hasExternalTicketId(): boolean {
        return !!this.betslip.externalTicketId && this.betslip.externalTicketId.length > 0;
    }

    get externalTicketLink(): string {
        const externalTicketId = this.betslip.externalTicketId ? this.betslip.externalTicketId.replace(new RegExp('-', 'g'), '') : '';

        return this.sitecoreMyBets.myBets.AamsLink.replace('{0}', externalTicketId);
    }

    get hasTaxationInfo(): boolean {
        if (this.betslip.isCachedOut || (!this.userSettings.isTurnoverTaxApplied && this.betslip.isFreeBet) || !this.myBetsConfig.showNetWinnings) {
            return false;
        }
        if (this.userSettings.isTurnoverTaxApplied && !!this.betslip.taxInformation?.taxes) {
            return true;
        }

        // Show tax info if there is Tax amount OR Liability amount from HBS (GR: tax = 0, liab > 0)
        const tax = this.betslip.taxInformation?.taxes.find((s) => s.model === TaxationModel.OnWinnings)?.amount.value;
        const liability = this.betslip.taxInformation?.additionalInformation?.informationItems.find((item) => item.key === 'liability')?.value;

        return Number(tax) > 0 || Number(liability) > 0;
    }

    get betPlacedDate(): Date {
        return this.betslip.betPlacedDate;
    }

    get betslipId(): string {
        return this.betslip.betslipId;
    }

    get hasButtons(): boolean {
        return this.showAlertButton || this.showPinButton;
    }

    get showAlertButton(): boolean {
        return !!(this.alertData && this.alertData.bets.length);
    }

    get showPinButton(): boolean {
        return this.myBetsConfig.enablePinning && this.betslip.state === SlipState.Open;
    }

    get showShareMyBetButton(): boolean {
        const isBetbuilder = this.betslip.bets.some((x) => x.betBuilderInfo);
        const isVirtual = this.betslip.bets[0].isVirtual;
        const isNewCustomerOffer = this.customerOfferService.isNewCustomerOffer(this.betslip.bets[0].leagueId);

        return this.betslip.state === SlipState.Open && !isBetbuilder && !isVirtual && !isNewCustomerOffer && !this.betslip.isTeaserBet;
    }

    get isToolbarVisible(): boolean {
        return !this.editBetHistoryService.getIsToolbarRead();
    }

    get hasAutoCashoutInfo(): boolean {
        if (this.betslip.earlyPayoutInformation) {
            return this.betslip.earlyPayoutInformation.autoCashoutTriggered;
        } else {
            return false;
        }
    }

    get autoCashoutValue(): string {
        if (this.betslip.earlyPayoutInformation) {
            return this.moneyService.transform(
                this.betslip.earlyPayoutInformation.autoCashoutThresholdValue ? this.betslip.earlyPayoutInformation.autoCashoutThresholdValue : '',
                true,
                2,
                false,
                '',
                this.betslip.stake.currency,
            );
        } else {
            return '';
        }
    }

    get placedBetInfo(): IPlacedBetInfo {
        return {
            betslipId: this.betslip.betslipRealId,
            picksParams: this.convertFromBets(this.betslip.bets),
            location: MyBetsTrackingLocation,
            betType: this.getBetType(this.betslip.betType),
            systemType: this.betslip.systemType ? this.betslip.typeTranslated : '',
            eventName: this.betslip.bets[0] && this.betslip.bets[0].eventName,
            pickCount: this.betslip.bets.length,
        };
    }

    private get element(): HTMLElement {
        return this.elementRef.nativeElement;
    }

    private closeCashoutTooltip = (event: Event): void => {
        if (this.isOwnEvent(event) && this.isCloseClick(event)) {
            return;
        }
        this.updateCashoutInfoVisibility(false);
    };

    private updateCashoutInfoVisibility(isVisible: boolean): void {
        this.cashoutInfoVisible = isVisible;
        if (isVisible) {
            this._window.addEventListener('click', this.closeCashoutTooltip, true);
        } else {
            this._window.removeEventListener('click', this.closeCashoutTooltip, true);
        }
    }

    private isCloseClick(event: Event): boolean {
        if (event && event.target) {
            const target = <HTMLElement>event.target;
            const iconClick = target.className.indexOf('cashout-unavailable-icon') > -1 || target.className.indexOf('cashout-unavailable-label') > -1;
            const closeButtonClick = target.className.indexOf('cashout-unavailable-close') > -1 || target.className.indexOf('theme-ex') > -1;

            return iconClick || closeButtonClick;
        }

        return false;
    }

    private isOwnEvent(event: Event): boolean {
        return !!event.target && this.element.contains(<Node>event.target);
    }

    private getBetType(type: BetSlipType): string {
        if (type === BetSlipType.Single) {
            return 'Single';
        }

        if (type === BetSlipType.Combo) {
            return 'Combo';
        }

        return 'System';
    }

    private convertFromBets(bets: MyBetsBet[]): OptionQuery[] {
        const options: OptionQuery[] = [];

        bets.forEach((bet) => {
            if (bet.resultInfo) {
                options.push({
                    fixture: bet.resultInfo.eventId.toString(),
                    optionGroup: bet.resultInfo.marketId,
                    option: bet.resultInfo.optionId,
                });
            }
            if (bet.optionMarketInfo) {
                options.push({
                    fixture: bet.optionMarketInfo.fixtureCompoundId,
                    optionGroup: bet.optionMarketInfo.optionMarketId,
                    option: bet.optionMarketInfo.optionId,
                    priceId: bet.optionMarketInfo.priceId,
                });
            }
            if (bet.participantInfo) {
                // TODO: remove if HBS start providing marketId for race picks.
                const marketId = bet.participantInfo.marketId ? +bet.participantInfo.marketId : 0;

                options.push({
                    fixture: bet.participantInfo.fixtureCompoundId,
                    optionGroup: marketId,
                    option: +Object.keys(bet.participantInfo.participantInfo)[0],
                });
            }
        });

        return options;
    }

    private getAlertData(): { bets: AlertData[] } {
        const alertData: AlertData[] = [];

        if (this.nativeAlertService.isEnabled()) {
            for (const bet of this.betslip.bets) {
                if (
                    bet.resultInfo &&
                    bet.resultInfo.event &&
                    bet.resultInfo.event.liveAlert &&
                    this.nativeAlertService.isSportEnabled(bet.resultInfo.event.sport.id)
                ) {
                    const event = bet.resultInfo.event;
                    alertData.push({
                        liveAlert: event.liveAlert || false,
                        sportsId: event.sport.id,
                        eventId: event.id,
                        leagueId: event.league.id,
                        eventName: event.name,
                        eventGroupId: event.groupId,
                        leagueGroupId: event.league.parentLeagueId,
                        containsLiveBets: true,
                    });
                }
            }
        }

        return {
            bets: alertData,
        };
    }

    private getTaxationInfo(): string {
        const liability = this.betslip.taxInformation.additionalInformation?.informationItems.find((item) => item.key === 'liability');

        if (liability) {
            return this.getTaxationInfoWithLiability();
        }

        const grossWinnings = `${this.getGrossWinnings()} (${this.sitecoreMyBets.myBets.Gross})`;
        const taxationMessage = `${this.getTaxation()} (${this.sitecoreMyBets.myBetsTax.TaxPercentage})`;

        return `${this.sitecoreMyBets.myBets.MyBetsNetWinnings} ${grossWinnings} - ${taxationMessage}`;
    }

    private getTaxationInfoWithLiability(): string {
        const grossWinnings = `${this.getGrossWinnings()} (${this.sitecoreMyBets.myBets.GrossWinnings})`;
        const taxationMessage = `${this.getTaxation()} (${this.sitecoreMyBets.myBetsTax.TaxDeductionMessage})`;
        const liabilityMessage = `${this.getLiability()} (${this.sitecore.betslip.messages.TaxLiability})`;

        return `${grossWinnings} , ${taxationMessage} , ${liabilityMessage}`;
    }

    private getTaxation(): string {
        const tax = this.betslip.taxInformation.taxes.find((s) => s.model === TaxationModel.OnWinnings)?.amount.value;

        return this.moneyService.transform(tax || 0, true, 2);
    }

    private getGrossWinnings(): string {
        return this.moneyService.transform(
            this.betslip.state === SlipState.Open ? this.betslip.grossPossibleWinnings.value : this.betslip.grossPayoutAmount.value,
            true,
            2,
        );
    }

    private getLiability(): string {
        const liability = this.betslip.taxInformation.additionalInformation.informationItems.find((item) => item.key === 'liability')?.value;

        return this.moneyService.transform(liability || 0, true, 2);
    }

    private getValueWithCurrency(value: number): string {
        return value + ' ' + this.betslip.grossPayoutAmount.currency;
    }

    get getGrossStake(): string {
        const data = this.betslip.stake.value + this.betslipUtilService.getTaxAmount(this.betslip, TaxationModel.OnStake);

        return this.getValueWithCurrency(this.betslipUtilService.getRounding(new Decimal(data)));
    }

    get getTurnoverTax(): string {
        return this.getValueWithCurrency(this.betslipUtilService.getTaxAmount(this.betslip, TaxationModel.OnStake));
    }

    get getStakeNet(): string {
        return this.betslip.stake.value + ' ' + this.betslip.stake.currency;
    }

    get getGrossWinning(): string {
        return this.getGrossWinnings();
    }

    get getWinningTax(): string {
        return this.getValueWithCurrency(this.betslipUtilService.getTaxAmount(this.betslip, TaxationModel.OnWinnings));
    }

    get getWinningNet(): string {
        return this.betslipUtilService.possibleWinningsValue(this.betslip);
    }

    get showTurnoverTax(): boolean {
        return !!this.betslipUtilService.getTaxAmount(this.betslip, TaxationModel.OnStake);
    }

    get showWinningTax(): boolean {
        return this.betslip.betType === BetSlipType.System && this.betslip.state === SlipState.Open
            ? false
            : !!this.betslipUtilService.getTaxAmount(this.betslip, TaxationModel.OnWinnings);
    }

    get showTaxDetails(): boolean {
        return this.userSettings.isTurnoverTaxApplied;
    }

    private readonly _window = inject(WINDOW);
}
